import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './RecipeDetail.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { recipes } from '../../components/Recipes';

// Interface for the API response data
interface RecipeData {
  _id: string;
  recipeId: string;
  lang: string;
  foodName: string;
  ingredients: string[];
  instructions: string[];
}

// Interface for the API response structure
interface ApiResponse {
    success: boolean;
    data: RecipeData | null;
    message?: string;
}

// Interface for the static recipe data
interface RecipeMeta {
  difficulty: string;
  time: string;
  category: string;
  cuisine: string;
}

const RecipeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>(); // This is the videoId
  const { t, i18n } = useTranslation();
  const [recipe, setRecipe] = useState<RecipeData | null>(null);
  const [meta, setMeta] = useState<RecipeMeta | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecipe = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);
      
      const language = i18n.language.split('-')[0];

      try {
        const response = await fetch(`https://bs.foodquick.app/api/api/recipe-translations/recipe/same/${id}/${language}`);
        
        let result: ApiResponse;
        if (!response.ok) {
            const fallbackResponse = await fetch(`https://bs.foodquick.app/api/api/recipe-translations/recipe/same/${id}/en`);
            if(!fallbackResponse.ok){
                throw new Error('Network response was not ok for both languages');
            }
            result = await fallbackResponse.json();
        } else {
            result = await response.json();
        }

        if (result.success && result.data) {
            const recipeData = result.data;
            setRecipe(recipeData);
            const recipeMeta = recipes.find(r => r.videoId === recipeData.recipeId);
            if (recipeMeta) {
                setMeta({
                    difficulty: recipeMeta.difficulty,
                    time: recipeMeta.time,
                    category: recipeMeta.category,
                    cuisine: recipeMeta.cuisine
                });
            }
        } else {
            const fallbackResponse = await fetch(`https://bs.foodquick.app/api/api/recipe-translations/recipe/same/${id}/en`);
            const fallbackResult: ApiResponse = await fallbackResponse.json();
            if (fallbackResult.success && fallbackResult.data) {
                const fallbackData = fallbackResult.data;
                setRecipe(fallbackData);
                const recipeMeta = recipes.find(r => r.videoId === fallbackData.recipeId);
                if (recipeMeta) {
                    setMeta({
                        difficulty: recipeMeta.difficulty,
                        time: recipeMeta.time,
                        category: recipeMeta.category,
                        cuisine: recipeMeta.cuisine
                    });
                }
            } else {
                throw new Error('Failed to fetch recipe data.');
            }
        }

      } catch (err) {
        setError(t('recipeDetail.error'));
        console.error("Failed to fetch recipe:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipe();
  }, [id, i18n.language, t]);

  if (loading) {
    return <div className="recipe-detail-loader">{t('recipeDetail.loading')}</div>;
  }

  if (error || !recipe) {
    return <div className="recipe-detail-loader">{error || t('recipeDetail.error')}</div>;
  }

  const embedUrl = `https://www.youtube.com/embed/${recipe.recipeId}`;

  return (
    <HelmetProvider>
        <Helmet>
            <title>{`${recipe.foodName} - FoodQuick`}</title>
            <meta name="description" content={`${t('recipeDetail.seoDescription')} ${recipe.foodName}. ${t('hero.description')}`} />
            <meta property="og:title" content={`${recipe.foodName} - FoodQuick`} />
            <meta property="og:description" content={`${t('recipeDetail.seoDescription')} ${recipe.foodName}.`} />
            <meta property="og:image" content={`https://i.ytimg.com/vi/${recipe.recipeId}/hqdefault.jpg`} />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="website" />
        </Helmet>
        <Header />
        <div className="recipe-detail-container">
            <div className="recipe-detail-card">
                <div className="video-container">
                    <iframe
                        src={embedUrl}
                        title={recipe.foodName}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    ></iframe>
                </div>

                <header className="recipe-detail-header">
                    <h1>{recipe.foodName}</h1>
                    {meta && (
                        <div className="recipe-meta-info">
                            <span className="meta-tag cuisine">&#x1F3F4; {t(`recipes.cuisines.${meta.cuisine.toLowerCase()}`)}</span>
                            <span className="meta-tag category">&#x1F370; {t(`recipes.categories.${meta.category.toLowerCase()}`)}</span>
                            <span className="meta-tag difficulty">&#x1F6A7; {t(`recipes.difficulties.${meta.difficulty.toLowerCase()}`)}</span>
                            <span className="meta-tag time">&#x23F0; {meta.time} {t('recipes.time_unit')}</span>
                        </div>
                    )}
                </header>

                <div className="recipe-body">
                    <div className="ingredients-section">
                        <h3>{t('recipeDetail.ingredients')}</h3>
                        <ul>
                            {recipe.ingredients.map((ingredient, index) => (
                                <li key={index}>{ingredient}</li>
                            ))}
                        </ul>
                    </div>

                    <div className="instructions-section">
                        <h3>{t('recipeDetail.instructions')}</h3>
                        <ol>
                            {recipe.instructions.map((step, index) => (
                                <li key={index}>{step}</li>
                            ))}
                        </ol>
                    </div>
                </div>
            </div>
        </div>
        <Footer />
    </HelmetProvider>
  );
};

export default RecipeDetail;
